import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * Selection state with shift-range semantics.
 *
 * `toggle(id, range=false)` — flip a single id.
 * `toggle(id, range=true)`  — extend selection from the last toggled id to
 *                             this one (Gmail / Linear pattern).
 * `clear()`                 — empty the set.
 * `setAll(ids)`             — replace selection with the given ids.
 */
export function useBulkSelection<TId extends string>() {
  const [selected, setSelected] = useState<Set<TId>>(() => new Set())
  const lastIdRef = useRef<TId | null>(null)

  const toggle = useCallback(
    (id: TId, range: boolean, allOrderedIds: readonly TId[] = []) => {
      setSelected((prev) => {
        const next = new Set(prev)
        if (range && lastIdRef.current && allOrderedIds.length > 0) {
          const startIdx = allOrderedIds.indexOf(lastIdRef.current)
          const endIdx = allOrderedIds.indexOf(id)
          if (startIdx >= 0 && endIdx >= 0) {
            const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx]
            for (let i = from; i <= to; i++) next.add(allOrderedIds[i]!)
            lastIdRef.current = id
            return next
          }
        }
        if (next.has(id)) next.delete(id)
        else next.add(id)
        lastIdRef.current = id
        return next
      })
    },
    [],
  )

  const clear = useCallback(() => {
    setSelected(new Set())
    lastIdRef.current = null
  }, [])

  const setAll = useCallback((ids: readonly TId[]) => {
    setSelected(new Set(ids))
  }, [])

  const isSelected = useCallback((id: TId) => selected.has(id), [selected])

  return useMemo(
    () => ({
      selected,
      toggle,
      clear,
      setAll,
      isSelected,
      count: selected.size,
    }),
    [selected, toggle, clear, setAll, isSelected],
  )
}
